// T042 — Paginated, sortable, filterable, searchable wallet list state.
//
// Treats the listing endpoint's 404 as a deployment signal (`endpointMissing`)
// so the page can degrade to manual-lookup mode without spamming errors.
import { useCallback, useEffect, useRef, useState } from "react";

import { adminListWallets } from "../services/wallet/adminWalletApi";
import type {
  WalletListItem,
  WalletListQuery,
} from "../types/wallet";

const DEFAULT_LIMIT = 50;

interface UseAdminWalletListResult {
  query: WalletListQuery;
  setQuery: (patch: Partial<WalletListQuery>) => void;
  items: WalletListItem[];
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  endpointMissing: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

const isQueryShapeChange = (a: WalletListQuery, b: WalletListQuery): boolean =>
  a.search !== b.search ||
  a.balance !== b.balance ||
  a.sort !== b.sort ||
  a.limit !== b.limit;

const useAdminWalletList = (): UseAdminWalletListResult => {
  const [query, setQueryState] = useState<WalletListQuery>({
    sort: "last_activity_desc",
    balance: "any",
    limit: DEFAULT_LIMIT,
  });
  const [items, setItems] = useState<WalletListItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [endpointMissing, setEndpointMissing] = useState(false);
  const inFlight = useRef(0);

  const fetchPage = useCallback(
    async (q: WalletListQuery, append: boolean) => {
      const ticket = ++inFlight.current;
      setLoading(true);
      setError(null);
      try {
        const response = await adminListWallets(q);
        if (ticket !== inFlight.current) return;
        setItems((prev) => (append ? [...prev, ...response.items] : response.items));
        setCursor(response.next_cursor);
        setHasMore(Boolean(response.next_cursor));
        setEndpointMissing(false);
      } catch (err: any) {
        if (ticket !== inFlight.current) return;
        const status = err?.response?.status;
        if (status === 404) {
          setEndpointMissing(true);
          setItems([]);
          setHasMore(false);
        } else {
          console.error("Wallet list fetch failed", err);
          setError(
            err?.response?.data?.message ??
              "Failed to load wallets. Try again or check the Payment service.",
          );
        }
      } finally {
        if (ticket === inFlight.current) setLoading(false);
      }
    },
    [],
  );

  // Initial load + refetch on shape changes (search, sort, balance, limit).
  // Cursor is intentionally NOT a dep — only loadMore() advances the cursor.
  useEffect(() => {
    fetchPage(
      {
        search: query.search,
        balance: query.balance,
        sort: query.sort,
        limit: query.limit,
      },
      false,
    );
  }, [query.search, query.balance, query.sort, query.limit, fetchPage]);

  const setQuery = useCallback(
    (patch: Partial<WalletListQuery>) => {
      setQueryState((prev) => {
        const next = { ...prev, ...patch };
        // Drop cursor whenever the shape changes — fresh page.
        if (isQueryShapeChange(prev, next)) {
          next.cursor = undefined;
        }
        return next;
      });
    },
    [],
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || loading || !cursor) return;
    await fetchPage({ ...query, cursor }, true);
  }, [cursor, fetchPage, hasMore, loading, query]);

  const refresh = useCallback(async () => {
    await fetchPage({ ...query, cursor: undefined }, false);
  }, [fetchPage, query]);

  return {
    query,
    setQuery,
    items,
    loading,
    hasMore,
    error,
    endpointMissing,
    loadMore,
    refresh,
  };
};

export default useAdminWalletList;
